/**
 * Authentication utilities for Keycloak integration
 */
import { API_BASE_URL, DEBUG, SESSION_TIMEOUT as CONFIG_SESSION_TIMEOUT, getApiUrl, secureLog } from '../config';
import toast from 'react-hot-toast';
import keycloak from './keycloakauth';

/**
 * The session timeout duration in seconds
 * Using the value from config.js
 */
export const SESSION_TIMEOUT = CONFIG_SESSION_TIMEOUT;

/**
 * Gets the API base URL
 * @returns {string} The API base URL
 */
export const getApiBaseUrl = () => API_BASE_URL;

/**
 * Refreshes the Keycloak token if needed
 * @param {object} keycloak - The Keycloak instance
 * @param {number} minValidity - Minimum token validity in seconds (default: 30)
 * @returns {Promise<boolean>} - True if token was refreshed, false if not needed
 * @throws {Error} If token refresh fails
 */
export const refreshToken = async (keycloak, minValidity = 30) => {
  try {
    // Using refresh token when necessary
    const refreshed = await keycloak.updateToken(minValidity);
    
    if (refreshed) {
      secureLog.auth('Token was successfully refreshed');
      
      // Check and log token expiration time for debugging
      if (keycloak.tokenParsed && keycloak.tokenParsed.exp) {
        const expiryDate = new Date(keycloak.tokenParsed.exp * 1000);
        const now = new Date();
        const minutesUntilExpiry = Math.round((expiryDate - now) / 60000);
        secureLog.auth(`Token will expire in ${minutesUntilExpiry} minutes (${expiryDate.toISOString()})`);
      }
    }
    return refreshed;
  } catch (error) {
    secureLog.error('Failed to refresh token', error);
    throw error;
  }
};

/**
 * Performs an authenticated API request with automatic token refresh
 * @param {string} endpoint - The endpoint to fetch
 * @param {object} options - Fetch options (method, body, etc.)
 * @param {string} token - The authentication token to use
 * @returns {Promise<Response>} - The fetch response
 * @throws {Error} If request fails or authentication is invalid
 */
export const authenticatedFetch = async (endpoint, options = {}, token = null) => {
  try {
    // Always use fresh token from Keycloak instead of potentially stale passed token
    let authToken = keycloak?.token;
    
    if (!authToken) {
      secureLog.error('No authentication token provided');
      throw new Error('No authentication token provided');
    }

    // Construct the full URL using the helper function
    const url = getApiUrl(endpoint);
    
    // Add request ID for tracking in logs
    const requestId = Math.random().toString(36).substring(2, 15);
    secureLog.info(`[${requestId}] Making authenticated request to: ${url}`, {
      method: options.method || 'GET',
      endpoint,
      hasToken: !!authToken
    });

    // Only refresh if token is actually expired (not just about to expire)
    if (keycloak && keycloak.tokenParsed) {
      const currentTime = Math.floor(Date.now() / 1000);
      const tokenExpiry = keycloak.tokenParsed.exp;
      
      // Only refresh if token is actually expired (not preventatively)
      if (tokenExpiry && tokenExpiry <= currentTime) {
        secureLog.auth(`[${requestId}] Token is expired. Expiry: ${tokenExpiry}, Current: ${currentTime}. Attempting to refresh...`);
        try {
          const refreshed = await keycloak.updateToken(30);
          if (refreshed) {
            secureLog.auth(`[${requestId}] Token successfully refreshed`);
            authToken = keycloak.token; // Use the fresh token
          } else {
            secureLog.auth(`[${requestId}] Token still valid, no refresh needed`);
          }
        } catch (refreshError) {
          secureLog.error(`[${requestId}] Failed to refresh token:`, refreshError);
          throw new Error('Authentication token expired and refresh failed');
        }
      }
    }

    const response = await fetch(url, {
      ...options,
      headers: {
        // Only set Content-Type if we're not sending FormData
        ...(!(options.body instanceof FormData) && { 'Content-Type': 'application/json' }),
        'Authorization': `Bearer ${authToken}`,
        ...options.headers,
      },
    });

    // Log response status
    secureLog.info(`[${requestId}] Response received: ${response.status} ${response.statusText}`, {
      endpoint,
      status: response.status
    });

    if (!response.ok) {
      if (response.status === 401) {
        secureLog.auth(`[${requestId}] Authentication failed (401) - checking authorization status`);
        
        // Mark user as unauthorized
        localStorage.setItem('isUserAuthorized', 'false');
        
        // Check if user was previously authorized - this indicates access was revoked
        const wasAuthorized = sessionStorage.getItem('wasAuthorized') === 'true';
        if (wasAuthorized) {
          secureLog.auth(`[${requestId}] User previously had access but received 401. Logging out...`);
          
          // Clear auth data
          localStorage.removeItem('isUserAuthorized');
          localStorage.removeItem('adminUsers');
          sessionStorage.removeItem('wasAuthorized');
          
          // Clear any other cached data
          Object.keys(localStorage).forEach(key => {
            if (key.startsWith('auth_') || key.startsWith('user_')) {
              localStorage.removeItem(key);
            }
          });
          
          // Redirect to no-access page if not on a safe page
          const safePaths = ['/no-access', '/integrations', '/login', '/dashboards'];
          const isSafePath = safePaths.some(path => window.location.pathname.startsWith(path));
          
          if (!isSafePath) {
            window.location.href = '/no-access';
            return new Response(JSON.stringify({ detail: "Authentication failed, access revoked" }), {
              status: 401,
              headers: { 'Content-Type': 'application/json' }
            });
          }
        }
        
        // If Keycloak is available, try to refresh the token and retry the request
        if (keycloak && keycloak.token) {
          try {
            secureLog.auth(`[${requestId}] Attempting token refresh after 401...`);
            const refreshed = await keycloak.updateToken(60);
            
            if (refreshed) {
              secureLog.auth(`[${requestId}] Token refreshed after 401, retrying request`);
              
              // Retry the request with the new token
              const retryResponse = await fetch(url, {
                ...options,
                headers: {
                  // Only set Content-Type if we're not sending FormData
                  ...(!(options.body instanceof FormData) && { 'Content-Type': 'application/json' }),
                  'Authorization': `Bearer ${keycloak.token}`,
                  ...options.headers,
                },
              });
              
              secureLog.info(`[${requestId}] Retry response: ${retryResponse.status}`);
              
              // If retry succeeded, return the response without throwing an error
              if (retryResponse.ok) {
                secureLog.auth(`[${requestId}] Retry succeeded`);
                return retryResponse;
              }
              
              // If retry failed with 401 again, log out the user
              if (retryResponse.status === 401) {
                secureLog.auth(`[${requestId}] Retry failed with 401 again, user needs to re-authenticate`);
                
                // Redirect to no-access page if not on a safe page
                const safePaths = ['/no-access', '/integrations', '/login', '/dashboards'];
                const isSafePath = safePaths.some(path => window.location.pathname.startsWith(path));
                
                if (!isSafePath) {
                  window.location.href = '/no-access';
                  return new Response(JSON.stringify({ detail: "Authentication failed" }), {
                    status: 401,
                    headers: { 'Content-Type': 'application/json' }
                  });
                }
              }
              
              // If retry failed with 403, redirect to no-access
              if (retryResponse.status === 403) {
                secureLog.auth(`[${requestId}] Retry failed with 403, redirecting to no-access`);
                localStorage.setItem('isUserAuthorized', 'false');
                
                // Redirect to no-access page if not on a safe page
                const safePaths = ['/no-access', '/integrations', '/login', '/dashboards'];
                const isSafePath = safePaths.some(path => window.location.pathname.startsWith(path));
                
                if (!isSafePath) {
                  window.location.href = '/no-access';
                  return new Response(JSON.stringify({ detail: "Access forbidden" }), {
                    status: 403,
                    headers: { 'Content-Type': 'application/json' }
                  });
                }
              }
              
              // Throw an appropriate error
              throw new Error(`Retry failed with status: ${retryResponse.status}`);
            }
          } catch (refreshError) {
            secureLog.error(`[${requestId}] Failed to refresh token after 401:`, refreshError);
          }
        }
        
        throw new Error('Authentication failed');
      }
      
      // Handle 403 Forbidden
      if (response.status === 403) {
        secureLog.auth(`[${requestId}] Authorization failed (403) - User does not have permission`);
        localStorage.setItem('isUserAuthorized', 'false');
        
        // Redirect to no-access page if not on a safe page
        const safePaths = ['/no-access', '/integrations', '/login', '/dashboards'];
        const isSafePath = safePaths.some(path => window.location.pathname.startsWith(path));
        
        if (!isSafePath) {
          window.location.href = '/no-access';
          return new Response(JSON.stringify({ detail: "Access forbidden" }), {
            status: 403,
            headers: { 'Content-Type': 'application/json' }
          });
        }
      }
      
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return response;
  } catch (error) {
    secureLog.error('Authentication error:', error);
    throw error;
  }
};

/**
 * Gets user info from Keycloak token
 * @param {object} keycloak - The Keycloak instance
 * @returns {Promise<object>} User info object with backend data when available
 */
export const getUserInfo = async (keycloak) => {
  if (!keycloak || !keycloak.authenticated || !keycloak.tokenParsed) {
    return null;
  }

  const keycloakInfo = {
    id: keycloak.tokenParsed.sub,
    email: keycloak.tokenParsed.email,
    name: keycloak.tokenParsed.name || keycloak.tokenParsed.preferred_username,
    preferredUsername: keycloak.tokenParsed.preferred_username,
    roles: keycloak.tokenParsed.realm_access?.roles || [],
    // Don't include the actual token in returned object for security
  };

  try {
    // Get user info from backend (including persisted token)
    const response = await authenticatedFetch('/api/users/me', keycloak);
    if (response.ok) {
      const backendUser = await response.json();
      return {
        ...keycloakInfo,
        backendId: backendUser.id,
        // Don't expose backend token for security
      };
    }
  } catch (error) {
    secureLog.error('Error fetching user from backend:', error);
    // Silently fall back to Keycloak info
  }

  return keycloakInfo;
};

/**
 * Gets organization/tenant ID from Keycloak token
 * @param {object} keycloak - The Keycloak instance
 * @returns {string|null} Organization ID or null if not found
 */
export const getOrganizationId = (keycloak) => {
  if (!keycloak || !keycloak.authenticated || !keycloak.tokenParsed) {
    return null;
  }

  const token = keycloak.tokenParsed;
  
  // Check various possible locations for organization ID
  // These are common patterns used in Keycloak custom claims
  
  // Handle organizationId as array (take first element)
  if (token.organizationId) {
    if (Array.isArray(token.organizationId) && token.organizationId.length > 0) {
      console.log('Found organizationId array:', token.organizationId);
      console.log('Using first organization ID:', token.organizationId[0]);
      return String(token.organizationId[0]);
    }
    return String(token.organizationId);
  }
  
  // Direct claims (most common)
  if (token.organization_id) {
    if (Array.isArray(token.organization_id) && token.organization_id.length > 0) {
      return String(token.organization_id[0]);
    }
    return String(token.organization_id);
  }
  
  if (token.org_id) {
    if (Array.isArray(token.org_id) && token.org_id.length > 0) {
      return String(token.org_id[0]);
    }
    return String(token.org_id);
  }
  
  if (token.tenant_id) return String(token.tenant_id);
  if (token.tenant) return String(token.tenant);
  if (token.organization) return String(token.organization);
  if (token.org) return String(token.org);
  if (token.company_id) return String(token.company_id);
  if (token.client_id) return String(token.client_id);
  
  // Custom claims object
  if (token.custom_claims) {
    if (token.custom_claims.organizationId) {
      if (Array.isArray(token.custom_claims.organizationId) && token.custom_claims.organizationId.length > 0) {
        return String(token.custom_claims.organizationId[0]);
      }
      return String(token.custom_claims.organizationId);
    }
    
    if (token.custom_claims.organization_id) {
      if (Array.isArray(token.custom_claims.organization_id) && token.custom_claims.organization_id.length > 0) {
        return String(token.custom_claims.organization_id[0]);
      }
      return String(token.custom_claims.organization_id);
    }
    
    if (token.custom_claims.org_id) {
      if (Array.isArray(token.custom_claims.org_id) && token.custom_claims.org_id.length > 0) {
        return String(token.custom_claims.org_id[0]);
      }
      return String(token.custom_claims.org_id);
    }
    
    if (token.custom_claims.tenant_id) return String(token.custom_claims.tenant_id);
    if (token.custom_claims.organization) return String(token.custom_claims.organization);
    if (token.custom_claims.tenant) return String(token.custom_claims.tenant);
  }
  
  // Resource access (client-specific roles might contain org info)
  if (token.resource_access) {
    // Sometimes org ID is embedded in client names or roles
    const clientId = token.aud || token.azp; // audience or authorized party
    if (clientId && token.resource_access[clientId]) {
      const clientAccess = token.resource_access[clientId];
      if (clientAccess.organization_id) return clientAccess.organization_id;
      if (clientAccess.org_id) return clientAccess.org_id;
    }
  }
  
  // Realm access (realm-level custom attributes)
  if (token.realm_access && token.realm_access.organization_id) {
    return token.realm_access.organization_id;
  }
  
  // Groups (organization might be encoded in group names)
  if (token.groups && Array.isArray(token.groups)) {
    for (const group of token.groups) {
      // Look for groups like "org_2083" or "tenant_2083"
      const orgMatch = group.match(/(?:org|tenant|organization)_(\d+)/i);
      if (orgMatch) return orgMatch[1];
    }
  }
  
  // Check for any other claims that might contain numeric IDs
  // This is a fallback for when org ID is stored in unexpected places
  for (const [key, value] of Object.entries(token)) {
    if (typeof value === 'string' || typeof value === 'number') {
      // Skip known standard claims
      if (['sub', 'iat', 'exp', 'nbf', 'jti', 'iss', 'aud', 'typ', 'azp'].includes(key)) {
        continue;
      }
      // Look for claims that might be organization IDs (numeric values)
      if (/^\d+$/.test(String(value))) {
        console.log(`Potential organization ID found in claim '${key}':`, value);
        // Don't automatically return this as it might be a timestamp or other ID
        // Log it for manual verification
      }
    }
  }
  
  // Final fallback: use the backend's DOC_SER_ORG_ID env var value exposed via VITE
  // This matches what the backend uses when organization_id is null on the user record.
  const envOrgId = import.meta.env.VITE_DOC_SER_ORG_ID;
  if (envOrgId) return String(envOrgId);

  return null;
};

/**
 * Gets collection name with organization prefix
 * @param {object} keycloak - The Keycloak instance  
 * @param {string} companyName - Company name for the collection
 * @returns {string|null} Collection name with organization prefix, or null if no org ID found
 */
export const getCollectionName = (keycloak, companyName) => {
  const orgId = getOrganizationId(keycloak);
  
  if (!orgId) {
    console.warn('No organization ID found in Keycloak token - cannot construct collection name');
    return null;
  }
  
  if (!companyName) {
    console.warn('No company name provided - cannot construct collection name');
    return null;
  }
  
  const sanitizedCompanyName = companyName.replace(/\s+/g, '_');
  return `${orgId}_${sanitizedCompanyName}`;
};

/**
 * Checks if user has the specified role
 * @param {object} keycloak - The Keycloak instance
 * @param {string} role - Role to check for
 * @returns {boolean} True if user has the role
 */
export const hasRole = (keycloak, role) => {
  if (!keycloak || !keycloak.authenticated || !keycloak.tokenParsed) {
    return false;
  }

  const roles = keycloak.tokenParsed.realm_access?.roles || [];
  return roles.includes(role);
};

/**
 * Performs a fetch request with a timeout to cancel long-running requests
 * @param {string} url - The URL to fetch
 * @param {object} options - Fetch options
 * @param {number} timeoutMs - Timeout in milliseconds (default: 120000 - 2 minutes)
 * @returns {Promise<Response>} The fetch response
 */
export const fetchWithTimeout = async (url, options = {}, timeoutMs = 120000) => {
  // Create an AbortController to handle the timeout
  const controller = new AbortController();
  const { signal } = controller;
  
  // Create a timeout that will abort the request if it takes too long
  const timeoutId = setTimeout(() => {
    secureLog.warn(`Request to ${url} timed out after ${timeoutMs}ms, aborting`);
    controller.abort();
  }, timeoutMs);
  
  try {
    // Add the signal to the fetch options
    const fetchOptions = {
      ...options,
      signal
    };
    
    // Perform the fetch with the timeout
    const response = await fetch(url, fetchOptions);
    return response;
  } catch (error) {
    // Handle aborted requests differently
    if (error.name === 'AbortError') {
      secureLog.error(`Fetch aborted due to timeout after ${timeoutMs}ms:`, url);
      throw new Error(`Request timed out after ${timeoutMs}ms`);
    }
    throw error;
  } finally {
    clearTimeout(timeoutId);
  }
};

// Add a more robust fetchWithRetry function that also includes timeout
export const fetchWithRetry = async (url, options = {}, token = null, maxRetries = 3, initialDelay = 1000, timeoutMs = 120000) => {
  let retries = 0;
  let delay = initialDelay;

  while (retries < maxRetries) {
    try {
      const fetchOptions = {
        ...options,
        headers: {
          ...options.headers,
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        }
      };

      // Use fetchWithTimeout instead of fetch
      const response = await fetchWithTimeout(url, fetchOptions, timeoutMs);
      
      // Check if response is ok before trying to parse it
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP error ${response.status}: ${errorText}`);
      }

      return response;
    } catch (error) {
      retries++;
      
      // Log the error with specific handling for content length mismatch and timeouts
      const isContentLengthError = error.message.includes('ERR_CONTENT_LENGTH_MISMATCH') || 
                                  error.message.includes('Failed to fetch');
      const isTimeoutError = error.message.includes('timed out');
      
      secureLog.warn(`Fetch attempt ${retries}/${maxRetries} failed:`, {
        url,
        error: error.message,
        isContentLengthError,
        isTimeoutError,
        willRetry: retries < maxRetries
      });

      // If we've reached max retries, throw the error
      if (retries >= maxRetries) {
        throw error;
      }

      // Use exponential backoff for retry delay
      await new Promise(resolve => setTimeout(resolve, delay));
      delay *= 2; // Exponential backoff
    }
  }
}; 
